import { useState, useEffect } from "react";
import { Cookie } from "lucide-react";
import { triggerHaptic } from "@/utils/haptics";
import { useIsMobile } from "@/hooks/use-mobile";

interface CookieConsentData {
  essential: boolean;
  analytics: boolean;
  timestamp: number;
  version: string;
}

const COOKIE_POLICY_VERSION = "1.0";
const CONSENT_STORAGE_KEY = "sendfeedback_cookie_consent";

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    
    if (!storedConsent) {
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      try {
        const consent: CookieConsentData = JSON.parse(storedConsent);
        if (consent.version !== COOKIE_POLICY_VERSION) {
          setShowBanner(true);
        }
      } catch {
        setShowBanner(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!showBanner) return;
    const timer = setTimeout(() => {
      const firstButton = document.querySelector('[data-cookie-action]') as HTMLElement;
      firstButton?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [showBanner]);

  const saveConsent = (analytics: boolean) => {
    const consent: CookieConsentData = {
      essential: true,
      analytics,
      timestamp: Date.now(),
      version: COOKIE_POLICY_VERSION,
    };

    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: consent }));
    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    if (isMobile) triggerHaptic('success');
    saveConsent(true);
  };

  const handleEssentialOnly = () => {
    if (isMobile) triggerHaptic('light');
    saveConsent(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-20 left-4 right-4 md:bottom-4 md:left-auto md:right-4 md:max-w-sm z-50 bg-card border border-border/60 rounded-xl shadow-lg animate-in slide-in-from-bottom-2 fade-in duration-200 pb-safe"
    >
      <div className="px-4 py-3 flex items-center gap-3">
        <Cookie className="h-4 w-4 text-muted-foreground shrink-0 hidden sm:block" />
        <p className="text-xs text-muted-foreground flex-1">
          We use cookies for auth & analytics.{" "}
          <a href="/cookie-policy" className="underline hover:text-foreground transition-colors">Learn more</a>
        </p>
        <div className="flex gap-2 shrink-0">
          <button
            data-cookie-action
            onClick={handleEssentialOnly}
            className="text-xs px-3 py-1.5 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          >
            Decline
          </button>
          <button
            data-cookie-action
            onClick={handleAcceptAll}
            className="text-xs px-3 py-1.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function to check if analytics cookies are consented
export const hasAnalyticsCookieConsent = (): boolean => {
  try {
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!storedConsent) return false;
    
    const consent: CookieConsentData = JSON.parse(storedConsent);
    return consent.analytics === true && consent.version === COOKIE_POLICY_VERSION;
  } catch {
    return false;
  }
};

// Helper function to reopen cookie settings
export const openCookieSettings = () => {
  localStorage.removeItem(CONSENT_STORAGE_KEY);
  window.location.reload();
};
